import React, { useMemo } from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { FlexiblePageInput } from '@/pages/types';
import { ReadonlyComponentRenderer } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/ReadonlyComponentRenderer';
import { ThemeProvider } from '@mutiny-pkg/bin-ui/src/rigidMicrosites/theme/ThemeProvider';
import { ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import { FlexiblePagePassword } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/FlexiblePagePassword';
import { RenderStage } from '@mutiny-pkg/champaign-schemas/src/schemas/micrositePages/render';
import { CustomComponentProvider } from '@mutiny-pkg/bin-ui/src/customComponents/CustomComponentProvider';
import { AnalyticsPageView } from '../AnalyticsPageView';
import { createCustomComponentAssetUrlResolvers } from './customComponentAssetUrls';
import './componentClasses.css';

type FlexiblePageRendererProps = {
  pageId: string;
};

export function FlexiblePageRenderer({ pageId }: FlexiblePageRendererProps) {
  const {
    componentTree,
    overrideVariables,
    password,
    passwordEnabled,
    renderStage,
    staticSiteDomain,
    bodySnippets,
    tailwindCss,
    customComponents,
    clientData,
    isPdfExport,
    isThumbnail,
  } = useData<FlexiblePageInput>();
  const preloadedCustomComponents = usePageContext().preloadedCustomComponents;
  const theme = componentTree.data as unknown as ThemeProperties;

  const filesystem = useMemo(
    () => ({
      artifacts: customComponents.artifacts,
      slugToCurrentSourceHash: customComponents.slugToCurrentSourceHash,
    }),
    [customComponents],
  );
  const { resolveCompiledUrl, resolveRuntimeUrl } = useMemo(
    () =>
      createCustomComponentAssetUrlResolvers({
        renderStage,
        staticSiteDomain,
        getPageOrigin: () => window.location.origin,
      }),
    [renderStage, staticSiteDomain],
  );

  return (
    <ThemeProvider theme={theme}>
      <AnalyticsPageView
        endpoint={clientData.analyticsEndpoint}
        companyId={clientData.analyticsCompanyId}
        assetId={clientData.analyticsAssetId}
        memberKey={clientData.analyticsMemberKey}
        clientApiToken={clientData.analyticsClientApiToken}
        recordSessionReplay={clientData.analyticsRecordSessionReplay}
        defaultOptOutPiiTracking={clientData.defaultOptOutPiiTracking}
        preview={clientData.preview}
      />
      {tailwindCss && <style data-tailwind-ssr dangerouslySetInnerHTML={{ __html: tailwindCss }} />}
      {bodySnippets && <div style={{ display: 'none' }} dangerouslySetInnerHTML={{ __html: bodySnippets }} />}
      <CustomComponentProvider
        filesystem={filesystem}
        theme={theme as unknown as Parameters<typeof CustomComponentProvider>[0]['theme']}
        overrideVariables={overrideVariables}
        isEditor={false}
        resolveCompiledUrl={resolveCompiledUrl}
        resolveRuntimeUrl={resolveRuntimeUrl}
        preloadedModules={preloadedCustomComponents}
        stateConfig={
          clientData.componentStateEndpoint && clientData.componentStateCapability
            ? {
                endpoint: clientData.componentStateEndpoint,
                capability: clientData.componentStateCapability,
                defaultOptOutPiiTracking: clientData.defaultOptOutPiiTracking,
              }
            : undefined
        }
      >
        <ReadonlyComponentRenderer
          componentTree={componentTree}
          overrideVariables={overrideVariables}
          staticAssetsBaseURL={staticSiteDomain}
          renderStage={renderStage}
          isPdfExport={isPdfExport}
          isThumbnail={isThumbnail}
        >
          {passwordEnabled && password && (
            <FlexiblePagePassword password={password} pageId={pageId} inPreview={renderStage === RenderStage.Preview} />
          )}
        </ReadonlyComponentRenderer>
      </CustomComponentProvider>
    </ThemeProvider>
  );
}
